/** The slice of `XMLHttpRequest` the upload uses, so a test can play the network */
export type UploadRequest = Pick<
  XMLHttpRequest,
  'status' | 'onload' | 'onerror' | 'onabort' | 'open' | 'setRequestHeader' | 'send' | 'abort'
> & { upload: Pick<XMLHttpRequestUpload, 'onprogress'> }

export interface UploadWithProgressProps {
  /** A presigned PUT URL from `util/upload` */
  url: string
  file: File
  /** Bytes sent so far */
  onProgress: (loaded: number) => void
  signal?: AbortSignal
  createRequest?: () => UploadRequest
}

/** Must match the CacheControl the presigned PutObjectCommand signs (`backend/graph/src/storage/storage-url.ts`) */
const cacheControl = 'public, max-age=31536000, immutable'

/**
 * PUT a file to a presigned URL, reporting progress. `fetch` has no upload progress, and a library is large enough
 * that the page must show it (front-door spec §6.1, Uploading). Resolves on a 2xx; rejects on anything else, on a
 * network failure, and with an `AbortError` when `signal` aborts.
 */
export const uploadWithProgress = ({
  url,
  file,
  onProgress,
  signal,
  createRequest = () => new XMLHttpRequest(),
}: UploadWithProgressProps): Promise<void> =>
  new Promise((resolve, reject) => {
    const request = createRequest()
    request.upload.onprogress = event => onProgress(event.loaded)
    request.onload = () => {
      if (request.status >= 200 && request.status < 300) {
        resolve()
        return
      }
      reject(new Error(`The upload was refused with status ${request.status}`))
    }
    request.onerror = () => reject(new Error('The upload failed on the network'))
    request.onabort = () => reject(new DOMException('The upload was cancelled', 'AbortError'))
    signal?.addEventListener('abort', () => request.abort(), { once: true })

    request.open('PUT', url)
    request.setRequestHeader('Content-Type', 'application/zip')
    request.setRequestHeader('Cache-Control', cacheControl)
    request.send(file)
  })
